import { Box } from "components/Box";
import Button from "components/Button";
import { Drawer } from "components/Drawer";
import { Flex } from "components/Flex";
import { FormInput } from "components/FormInput";
import { IconButton } from "components/IconButton";
import { TagInput } from "components/TagInput";
import { Text } from "components/Text";
import { compose } from "lodash/fp";
import { desaturate, lighten } from "polished";
import { useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";
import styled, { css, useTheme } from "styled-components";

const dim = compose(lighten(0.1), desaturate(0.1));

const ColorButton = styled.button<{ active: boolean; colorIndex: number }>`
  background-color: ${(props) => props.theme.blockColors[props.colorIndex]};
  width: 45px;
  height: 45px;
  display: block;

  border: 2px solid ${(props) => props.theme.blockColors[props.colorIndex]};
  ${(props) => {
    if (!props.active) {
      return "";
    }

    const c1 = props.theme.blockColors[props.colorIndex];
    const c2 = dim(c1);

    return css`
      background-image: linear-gradient(
        45deg,
        ${c1} 25%,
        ${c2} 25%,
        ${c2} 50%,
        ${c1} 50%,
        ${c1} 75%,
        ${c2} 75%,
        ${c2} 100%
      );
      background-size: 10px 10px;
    `;
  }};
  &:focus,
  &:active {
    outline: 0;
    `;

type DetailProps = {
  description: string;
  color: number;
  tags: string[];
};

type BlockDetailsProps = {
  description: string;
  color: number;
  tags: string[];
  onChange: (props: DetailProps) => void;
  onDelete: () => void;
  onCancel: () => void;
  onConfirm: () => void;
};

export const BlockDetails = ({
  description,
  color,
  tags,
  onChange,
  onDelete,
  onCancel,
  onConfirm,
}: BlockDetailsProps) => {
  const theme = useTheme();
  const desRef = useRef<HTMLInputElement>(null!);
  const [focus, setFocus] = useState(false);

  const setDescription = (val: string) => {
    onChange({
      description: val,
      color,
      tags,
    });
  };

  const setTags = (val: string[]) => {
    onChange({
      description,
      color,
      tags: val,
    });
  };

  const setColor = (val: number) => {
    onChange({
      description,
      color: val,
      tags,
    });
  };

  return (
    <Drawer forceActive={focus}>
      <Box p={3}>
        <Flex justifyContent="space-between" alignItems="center" mb={3}>
          <IconButton icon={FaTimes} color="body" onClick={onCancel} />
          <Flex alignItems="center">
            <Button onClick={onDelete} color="danger">
              Delete
            </Button>
            <Button ml={1} onClick={onConfirm}>
              Save
            </Button>
          </Flex>
        </Flex>
        <Text color="body" fontSize={4} fontWeight={600} mb={0}>
          Edit Item
        </Text>
      </Box>
      <Box p={3}>
        <form onSubmit={onConfirm}>
          <FormInput
            name="description"
            label="Description"
            value={description}
            onChange={(ev) => setDescription(ev.currentTarget.value)}
            autoComplete="off"
            ref={desRef}
            onClear={() => setDescription("")}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
          />
          <TagInput
            value={tags}
            onChange={(val) => setTags(val)}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
          />
          <Flex mb={3} flexWrap="wrap" justifyContent="center">
            {theme.blockColors.map((_x, i) => (
              <ColorButton
                key={i}
                colorIndex={i}
                type="button"
                onClick={() => setColor(i)}
                active={color === i}
              />
            ))}
          </Flex>
        </form>
      </Box>
    </Drawer>
  );
};
